// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The trajectory ribbon, in Three (M13-25).
//
// The last three seconds of the ball's path — `BallTrail`, a point per simulation step — drawn as
// a line through the playfield's frame in three dimensions, up the ramps the ball took, with a
// dot at every step the ball touched something. The Builder's canvas draws the same trail flat
// (`paintTrail`); on the bench it is a thing in the air beside the ball.
//
// One geometry, sized for the trail's capacity, rewritten every frame from the trail: three
// thousand points is nothing to write and a rebuild per frame would be a garbage heap.
//
// **Coloured by speed** (polish): each point takes its speed band's colour (`ribbonBands`) — cyan
// under a metre a second, the guide's pink up to three, the map's gold from three — so a slow
// roll across an inlane and a flipper's shot read differently at a glance.

import {
  BufferAttribute,
  BufferGeometry,
  CanvasTexture,
  Color,
  Group,
  LinearFilter,
  Line,
  LineBasicMaterial,
  Mesh,
  MeshBasicMaterial,
  PlaneGeometry,
  Points,
  PointsMaterial,
  SRGBColorSpace,
} from 'three'
import { TRAIL_STEPS, ribbonBands, type BallTrail } from '../engine/ballTrail'
import type { RibbonLegendRow } from '../xr/build/testTools'
import { FINISHES } from './partsKit'

export const RIBBON_COLOR = FINISHES.guide.color
/** The three speed bands' colours: slow, between, fast (`RIBBON_SLOW`, `RIBBON_FAST`). The headset's `BuildPicture.RibbonBandColours`. */
export const RIBBON_BAND_COLOURS: readonly number[] = [0x66e0ff, RIBBON_COLOR, 0xffd166]
export const CONTACT_COLOR = FINISHES.selected.color
/** A contact dot's size, world metres, attenuated with distance so it is a dot and not a billboard. */
export const CONTACT_SIZE = 0.006

/**
 * The key to the ribbon's colours (the test-tool follow-up, 25 Sep 2026), playfield metres: a small
 * card lying over the glass at the playfield's lower-left corner, where the flippers are not and a
 * player looking at the ball's path still sees it. `BuildPicture.RibbonLegend*` is the twin.
 */
export const LEGEND_WIDTH = 0.12
export const LEGEND_HEIGHT = 0.045
/** Its canvas: three rows of swatch and words. */
export const LEGEND_CANVAS = { width: 512, height: 192 } as const
/** In from the playfield's left edge and up from its bottom edge, and over the glass, metres. */
export const LEGEND_INSET = 0.012
export const LEGEND_UP = 0.07
export const LEGEND_OVER_GLASS = 0.012

/** Where the key lies, in the playfield's frame, for a playfield of this size under this glass. */
export function legendAt(width: number, length: number, glassHeight: number): { x: number; y: number; z: number } {
  return {
    x: -width / 2 + LEGEND_INSET + LEGEND_WIDTH / 2,
    y: -length / 2 + LEGEND_UP + LEGEND_HEIGHT / 2,
    z: glassHeight + LEGEND_OVER_GLASS,
  }
}

type DocumentLike = { createElement: (tag: string) => HTMLCanvasElement }

function legendCanvas(): HTMLCanvasElement | undefined {
  const host = (globalThis as { document?: DocumentLike }).document
  const canvas = host?.createElement('canvas')
  if (canvas === undefined) return undefined
  canvas.width = LEGEND_CANVAS.width
  canvas.height = LEGEND_CANVAS.height
  return canvas
}

function hexCss(hex: number): string {
  return `#${hex.toString(16).padStart(6, '0')}`
}

export interface BallRibbonBuild {
  readonly group: Group
  /** Draw the trail as it is now, or take the ribbon down (undefined); with the key's rows, the key too. */
  update(trail: BallTrail | undefined, legend?: readonly RibbonLegendRow[]): void
  /** The key's rows as last drawn, or nothing: the key is down. For a test with no renderer to read. */
  readonly legend: readonly RibbonLegendRow[] | undefined
  /** How many points were last drawn, for a test with no renderer to read. */
  readonly drawn: number
  readonly contacts: number
  dispose(): void
}

export function buildBallRibbon(
  capacity = TRAIL_STEPS,
  at: { readonly x: number; readonly y: number; readonly z: number } | undefined = undefined,
): BallRibbonBuild {
  const group = new Group()
  group.name = 'build.ribbon'
  group.visible = false
  // The key: one painted plane, repainted only when its words change (a language switch).
  const keyCanvas = at === undefined ? undefined : legendCanvas()
  const keyTexture = keyCanvas === undefined ? undefined : new CanvasTexture(keyCanvas)
  if (keyTexture !== undefined) {
    keyTexture.colorSpace = SRGBColorSpace
    keyTexture.generateMipmaps = false
    keyTexture.minFilter = LinearFilter
  }
  const keyGeometry = new PlaneGeometry(LEGEND_WIDTH, LEGEND_HEIGHT)
  const keyMaterial = new MeshBasicMaterial({
    ...(keyTexture === undefined ? { color: 0x101018 } : { map: keyTexture }),
    transparent: true,
    opacity: keyTexture === undefined ? 0.8 : 1,
    depthTest: false,
    depthWrite: false,
  })
  const key = new Mesh(keyGeometry, keyMaterial)
  key.name = 'ribbon-legend'
  key.renderOrder = 1003
  key.frustumCulled = false
  key.visible = false
  if (at !== undefined) {
    key.position.set(at.x, at.y, at.z)
    group.add(key)
  }
  let keyWords = ''
  let legendNow: readonly RibbonLegendRow[] | undefined
  const paintKey = (rows: readonly RibbonLegendRow[]): void => {
    const words = rows.map((r) => r.text).join('|')
    if (words === keyWords) return
    keyWords = words
    const ctx = keyCanvas?.getContext('2d')
    if (ctx === null || ctx === undefined || keyTexture === undefined) return
    const { width, height } = LEGEND_CANVAS
    ctx.clearRect(0, 0, width, height)
    ctx.fillStyle = 'rgba(10, 12, 20, 0.82)'
    ctx.fillRect(0, 0, width, height)
    const rowHeight = height / 3
    ctx.font = `${Math.round(rowHeight * 0.5)}px sans-serif`
    ctx.textBaseline = 'middle'
    rows.forEach((row, i) => {
      const y = rowHeight * i + rowHeight / 2
      ctx.fillStyle = hexCss(RIBBON_BAND_COLOURS[row.band] as number)
      ctx.fillRect(16, y - 5, 64, 10)
      ctx.fillStyle = '#f2f4ff'
      ctx.fillText(row.text, 96, y)
    })
    keyTexture.needsUpdate = true
  }
  const positions = new Float32Array(capacity * 3)
  const lineGeometry = new BufferGeometry()
  const lineAttribute = new BufferAttribute(positions, 3)
  lineGeometry.setAttribute('position', lineAttribute)
  const colours = new Float32Array(capacity * 3)
  const colourAttribute = new BufferAttribute(colours, 3)
  lineGeometry.setAttribute('color', colourAttribute)
  lineGeometry.setDrawRange(0, 0)
  const bands = new Uint8Array(capacity)
  const bandColours = RIBBON_BAND_COLOURS.map((hex) => new Color(hex))
  const lineMaterial = new LineBasicMaterial({ color: 0xffffff, vertexColors: true, transparent: true, opacity: 0.9, depthWrite: false })
  const line = new Line(lineGeometry, lineMaterial)
  line.name = 'ribbon-line'
  line.frustumCulled = false
  group.add(line)
  const marks = new Float32Array(capacity * 3)
  const dotGeometry = new BufferGeometry()
  const dotAttribute = new BufferAttribute(marks, 3)
  dotGeometry.setAttribute('position', dotAttribute)
  dotGeometry.setDrawRange(0, 0)
  const dotMaterial = new PointsMaterial({ color: CONTACT_COLOR, size: CONTACT_SIZE, sizeAttenuation: true, transparent: true, opacity: 0.95, depthWrite: false })
  const dots = new Points(dotGeometry, dotMaterial)
  dots.name = 'ribbon-contacts'
  dots.frustumCulled = false
  group.add(dots)
  let drawnNow = 0
  let contactsNow = 0

  return {
    group,
    update(trail, legend) {
      if (legend !== undefined && at !== undefined) {
        paintKey(legend)
        key.visible = true
        legendNow = legend
      } else {
        key.visible = false
        legendNow = undefined
      }
      if (trail === undefined || trail.length < 2) {
        group.visible = false
        legendNow = undefined
        drawnNow = 0
        contactsNow = 0
        lineGeometry.setDrawRange(0, 0)
        dotGeometry.setDrawRange(0, 0)
        return
      }
      group.visible = true
      let n = 0
      let c = 0
      trail.forEach((x, y, _step, contact, z) => {
        if (n >= capacity) return
        positions[n * 3] = x
        positions[n * 3 + 1] = y
        positions[n * 3 + 2] = z
        n++
        if (!contact) return
        marks[c * 3] = x
        marks[c * 3 + 1] = y
        marks[c * 3 + 2] = z
        c++
      })
      drawnNow = n
      contactsNow = c
      const banded = ribbonBands(trail, bands)
      for (let i = 0; i < banded; i++) {
        const colour = bandColours[bands[i] as number] as Color
        colours[i * 3] = colour.r
        colours[i * 3 + 1] = colour.g
        colours[i * 3 + 2] = colour.b
      }
      colourAttribute.needsUpdate = true
      lineAttribute.needsUpdate = true
      lineGeometry.setDrawRange(0, n)
      dotAttribute.needsUpdate = true
      dotGeometry.setDrawRange(0, c)
    },
    get drawn() {
      return drawnNow
    },
    get legend() {
      return legendNow
    },
    get contacts() {
      return contactsNow
    },
    dispose() {
      lineGeometry.dispose()
      lineMaterial.dispose()
      dotGeometry.dispose()
      dotMaterial.dispose()
      keyGeometry.dispose()
      keyMaterial.dispose()
      keyTexture?.dispose()
    },
  }
}
